import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLandingData } from "../../../hooks/useLandingData";

export default function ValueProposition() {
  const { features } = useLandingData();
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const colorMap: Record<string, { bg: string; text: string; border: string; metricColor: string }> = {
    amber: {
      bg: "bg-amber-50",
      text: "text-amber-600",
      border: "border-amber-100",
      metricColor: "text-amber-500",
    },
    emerald: {
      bg: "bg-emerald-50",
      text: "text-emerald-600",
      border: "border-emerald-100",
      metricColor: "text-emerald-500",
    },
  };

  return (
    <section
      className="py-28 bg-stone-50"
      style={{ background: "linear-gradient(180deg, #FAFAF8 0%, #F5F0EA 100%)" }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8" ref={ref}>
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-600 mb-4">
            {t("vp_label")}
          </span>
          <h2
            className="text-4xl lg:text-5xl font-bold text-stone-900 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("vp_title_1")}
            <br />
            <span className="text-amber-500">{t("vp_title_2")}</span>
          </h2>
          <p className="mt-4 text-stone-500 text-lg max-w-2xl mx-auto leading-relaxed">
            {t("vp_subtitle")}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {features.map((f, i) => {
            const c = colorMap[f.color] || colorMap.amber;
            return (
              <div
                key={f.title}
                className={`bg-white border border-stone-100 rounded-3xl p-8 hover:-translate-y-2 transition-all duration-500 cursor-default ${
                  visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div className={`w-14 h-14 flex items-center justify-center rounded-2xl ${c.bg} ${c.border} border mb-6`}>
                  <i className={`${f.icon} text-2xl ${c.text}`} />
                </div>
                <h3 className="text-xl font-bold text-stone-900 mb-3 leading-snug">{f.title}</h3>
                <p className="text-stone-500 text-sm leading-relaxed mb-8">{f.description}</p>
                <div className="pt-6 border-t border-stone-100 flex items-end gap-2">
                  <span className={`text-5xl font-black ${c.metricColor}`}>{f.metric}</span>
                  <span className="text-stone-400 text-sm mb-2 pb-1">{f.metricLabel}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16 bg-stone-900 rounded-3xl p-8 lg:p-12">
          <div className="grid lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3">
                {t("vp_arch_title")}
              </h3>
              <p className="text-stone-400 leading-relaxed">
                {t("vp_arch_desc")}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {[
                { icon: "ri-brain-line", label: "RAG Real-time" },
                { icon: "ri-calendar-2-line", label: "ISO 8601" },
                { icon: "ri-shield-check-line", label: "HIPAA Ready" },
              ].map((item) => (
                <div key={item.label} className="text-center p-4 bg-white/5 rounded-2xl border border-white/10">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-400/15 mx-auto mb-2">
                    <i className={`${item.icon} text-amber-400 text-xl`} />
                  </div>
                  <span className="text-stone-400 text-xs">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
